import type { ReportingRunEvent } from 'src/queries/reportingAgent/reportingAgent.models'
import {
  parseReportingRunEvent,
  parseReportingStreamCursor,
  safeRelativeUrl,
} from 'src/queries/reportingAgent/reportingAgent.validation'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'

export class ReportingAgentAccessDeniedError extends Error {
  readonly status: number

  constructor(status: number) {
    super('Reporting agent stream access denied')
    Object.setPrototypeOf(this, ReportingAgentAccessDeniedError.prototype)
    this.name = 'ReportingAgentAccessDeniedError'
    this.status = status
  }
}

export class ReportingAgentTransientError extends Error {
  readonly status: number

  constructor(status: number) {
    super('Reporting agent stream temporarily unavailable')
    Object.setPrototypeOf(this, ReportingAgentTransientError.prototype)
    this.name = 'ReportingAgentTransientError'
    this.status = status
  }
}

export interface ReportingRunEventStreamRequest {
  organizationId: string
  runId: string
  afterSequence?: number
  signal?: AbortSignal
  onEvent?: (event: ReportingRunEvent) => void
  fetchImpl?: typeof fetch
}

const pathSegment = (value: string, name: string): string => {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    !/^[A-Za-z0-9][A-Za-z0-9._:-]*$/.test(value)
  ) {
    throw new Error(`${name} is not a safe path id`)
  }
  return value
}

const organization = (value: string): string =>
  pathSegment(value, 'organizationId')

const query = (
  organizationId: string,
  afterSequence: number | undefined,
): string => {
  const params = new URLSearchParams({ organization_id: organizationId })
  if (afterSequence !== undefined)
    params.set('after_sequence', String(afterSequence))
  return params.toString()
}

export const resolveConfiguredAgentUrl = (
  relativePath: string,
  baseURL: string | undefined,
): string => {
  const safePath = safeRelativeUrl(relativePath, 'events URL')
  if (!baseURL) throw new Error('Reporting agent base URL is required')
  let base: URL
  try {
    base = new URL(baseURL)
  } catch {
    throw new Error('Reporting agent base URL is invalid')
  }
  if (
    (base.protocol !== 'https:' && base.protocol !== 'http:') ||
    base.username !== '' ||
    base.password !== ''
  ) {
    throw new Error('Reporting agent base URL is unsafe')
  }
  const basePath = base.pathname.endsWith('/')
    ? base.pathname
    : `${base.pathname}/`
  const resolved = new URL(`${basePath}${safePath.slice(1)}`, base.origin)
  if (resolved.origin !== base.origin)
    throw new Error('Reporting agent URL origin mismatch')
  return resolved.toString()
}

const frameParts = (
  frame: string,
): { event: string; id: string | null; data: unknown } => {
  let event: string | null = null
  let id: string | null = null
  const dataLines: string[] = []
  for (const line of frame.split(/\r?\n/)) {
    if (line.startsWith('event:')) {
      if (event !== null)
        throw new Error('SSE frame has duplicate event fields')
      event = line.slice(6).trimStart()
    } else if (line.startsWith('id:')) {
      if (id !== null) throw new Error('SSE frame has duplicate id fields')
      id = line.slice(3).trimStart()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart())
    } else if (line.trim() !== '') {
      throw new Error('SSE frame has an unknown field')
    }
  }
  if (!event) throw new Error('SSE frame has no event')
  const data = dataLines.join('\n')
  if (data.length === 0) throw new Error('SSE frame has no data')
  try {
    return { event, id, data: JSON.parse(data) as unknown }
  } catch {
    throw new Error('SSE frame data is not JSON')
  }
}

async function* bodyChunks(response: Response): AsyncGenerator<Uint8Array> {
  const body = response.body as ReadableStream<Uint8Array> | null
  if (!body) return
  const asyncBody = body as unknown as AsyncIterable<Uint8Array>
  if (typeof asyncBody[Symbol.asyncIterator] === 'function') {
    for await (const chunk of asyncBody) yield chunk
    return
  }
  const reader = body.getReader()
  try {
    while (true) {
      const result = await reader.read()
      if (result.done) return
      yield result.value
    }
  } finally {
    reader.releaseLock()
  }
}

export async function* streamReportingRunEvents({
  organizationId,
  runId,
  afterSequence,
  signal,
  onEvent,
  fetchImpl = fetch,
}: ReportingRunEventStreamRequest): AsyncGenerator<ReportingRunEvent> {
  if (
    afterSequence !== undefined &&
    (!Number.isInteger(afterSequence) || afterSequence < 0)
  ) {
    throw new Error('afterSequence must be a non-negative integer')
  }
  const org = organization(organizationId)
  const run = pathSegment(runId, 'runId')
  const requestConfig = getRequestConfig(RequestConfig.Agent)
  const url = resolveConfiguredAgentUrl(
    `/agents/runs/${run}/events/stream?${query(org, afterSequence)}`,
    requestConfig.baseURL,
  )
  const headers = {
    ...(requestConfig.headers as Record<string, string> | undefined),
    Accept: 'text/event-stream',
  }
  const response = await fetchImpl(url, { method: 'GET', headers, signal })
  if (
    response.status === 401 ||
    response.status === 403 ||
    response.status === 404
  ) {
    throw new ReportingAgentAccessDeniedError(response.status)
  }
  if (!response.ok) {
    if (response.status >= 500 && response.status <= 599)
      throw new ReportingAgentTransientError(response.status)
    throw new Error(`Reporting agent stream failed (${response.status})`)
  }

  const decoder = new TextDecoder()
  let pending = ''
  let previousSequence = afterSequence ?? 0
  let cleanlyClosed = false
  for await (const chunk of bodyChunks(response)) {
    pending += decoder.decode(chunk, { stream: true })
    const completeFrames = pending.split(/\r?\n\r?\n/)
    pending = completeFrames.pop() ?? ''
    for (const frame of completeFrames) {
      const parts = frameParts(frame)
      if (parts.event === 'stream.cursor') {
        if (parts.id !== null)
          throw new Error('stream cursor must not have an id')
        const cursor = parseReportingStreamCursor(parts.data)
        if (cursor.sequence !== previousSequence)
          throw new Error(
            'stream cursor sequence does not match the last accepted event',
          )
        cleanlyClosed = true
        return
      }
      if (parts.id === null || !/^\d+$/.test(parts.id))
        throw new Error('SSE event id is required and must be numeric')
      const event = parseReportingRunEvent(parts.data)
      if (parts.event !== event.type)
        throw new Error('SSE event name does not match payload type')
      if (Number(parts.id) !== event.sequence)
        throw new Error('SSE event id does not match sequence')
      if (event.run_id !== run) throw new Error('SSE event run id mismatch')
      if (event.sequence <= previousSequence)
        throw new Error('SSE sequence is not monotonic')
      previousSequence = event.sequence
      onEvent?.(event)
      yield event
      if (event.terminal) {
        cleanlyClosed = true
        return
      }
    }
  }
  if (pending.trim() !== '') {
    const parts = frameParts(pending)
    if (parts.event === 'stream.cursor') {
      if (parts.id !== null)
        throw new Error('stream cursor must not have an id')
      const cursor = parseReportingStreamCursor(parts.data)
      if (cursor.sequence !== previousSequence)
        throw new Error(
          'stream cursor sequence does not match the last accepted event',
        )
      cleanlyClosed = true
    } else {
      if (parts.id === null || !/^\d+$/.test(parts.id))
        throw new Error('SSE event id is required and must be numeric')
      const event = parseReportingRunEvent(parts.data)
      if (parts.event !== event.type)
        throw new Error('SSE event name does not match payload type')
      if (Number(parts.id) !== event.sequence)
        throw new Error('SSE event id does not match sequence')
      if (event.run_id !== run) throw new Error('SSE event run id mismatch')
      if (event.sequence <= previousSequence)
        throw new Error('SSE sequence is not monotonic')
      previousSequence = event.sequence
      onEvent?.(event)
      yield event
      cleanlyClosed = event.terminal
    }
  }
  if (!cleanlyClosed)
    throw new Error(
      'Reporting agent stream ended without terminal event or cursor',
    )
}

export const resumeReportingRunEvents = streamReportingRunEvents
